import React, { ReactNode, useContext } from 'react'

import { isAxiosError } from 'axios'

import { useToast } from '@vallourec-ui/vallourec-ui'

import { useTranslation } from 'react-i18next'

import {
  CustomFields,
  GenerateNewReport,
  RemarkReport,
  TestProps,
} from '@/@types/global'
import { Bending } from '@/@types/report/bending'
import { Chemical } from '@/@types/report/chemical'
import { CopperLayer } from '@/@types/report/copperLayer'
import { Ctod } from '@/@types/report/ctod'
import { Flaring } from '@/@types/report/flaring'
import { Flattening } from '@/@types/report/flattening'
import { FourPoint } from '@/@types/report/fourPoint'
import { Hardness } from '@/@types/report/hardness'
import { Hic } from '@/@types/report/hic'
import { Impact } from '@/@types/report/impact'
import { MicroCleanlines } from '@/@types/report/microCleanlines'
import { MicroHardness } from '@/@types/report/microHardness'
import { MicroStructure } from '@/@types/report/microStructure'
import { MicroStructurePhotos } from '@/@types/report/microStructurePhotos'
import { NaceA } from '@/@types/report/naceA'
import { NaceD } from '@/@types/report/naceD'
import { ReportItems } from '@/@types/report/report-items'
import { getSignatureUser, getTemplate } from '@/api/get'
import {
  sendToFlowApproval,
  usersNotifications,
  generateReportNew as generateReportNewPost,
} from '@/api/post'
import { updateReportDocument } from '@/api/put'
import { SITES, TESTS } from '@/utils/constants'
import {
  addSignatureInDD,
  downloadPdfFile,
  generateDocumentDefinition,
  getApprovalReport,
  getBase64FromUrl,
  getFilePDF,
  getGeneralReport,
  getReaderReport,
  getReportNumber,
} from '@/utils/pdfGenerator/pdfGenerator'
import { getTest } from '@/utils/testReport'
import { dealWithFile, getDateNow, getDateTimeNow } from '@/utils/utils'
import { orderBy } from 'lodash'

interface GenerateReportContextInterface {
  site: string
  setSite: (site: string) => void
  mappedSites: { key: number; value: string; label: string }[]
  test: string
  setTest: (test: string) => void
  mappedTests: { key: number; value: string; label: string }[]
  tests: string[]
  setTests: (tests: string[]) => void
  startDate: Date | undefined
  setStartDate: (startDate: Date | undefined) => void
  endDate: Date | undefined
  setEndDate: (endDate: Date | undefined) => void
  project: string
  setProject: (project: string) => void
  projects: string[]
  setProjects: (projects: string[]) => void
  orderItem: string
  setOrderItem: (orderItem: string) => void
  orderItems: string[]
  setOrderItems: (orderItems: string[]) => void
  typeInspection: string
  setTypeInspection: (typeInspection: string) => void
  typeInspections: string[]
  setTypeInspections: (typeInspections: string[]) => void
  type: string
  setType: (type: string) => void
  types: string[]
  setTypes: (types: string[]) => void
  clientHeader: string
  setClientHeader: (clientHeader: string) => void
  descriptionHeader: string
  setDescriptionHeader: (descriptionHeader: string) => void
  projectHeader: string
  setProjectHeader: (projectHeader: string) => void
  inspectionHeader: string
  setInspectionHeader: (inspectionHeader: string) => void
  pedidoItemHeader: string
  setPedidoItemHeader: (pedidoItemHeader: string) => void
  currentItem: ReportItems | undefined
  setCurrentItem: (value: ReportItems | undefined) => void
  testSelected: TestProps | undefined
  setTestSelected: (value: TestProps | undefined) => void
  heatItems: string[] | undefined
  setHeatItems: (value: string[] | undefined) => void
  processOrderItems: string[] | undefined
  setProcessOrderItems: (value: string[] | undefined) => void
  pqLotItems: string[] | undefined | null
  setPqLotItems: (value: string[] | undefined | null) => void
  resultItemsSelected: ReportItems[] | undefined
  setResultItemsSelected: (value: ReportItems[] | undefined) => void
  remark1: string | undefined
  setRemark1: (value: string) => void
  remark2: string | undefined
  setRemark2: (value: string) => void
  remark3: string | undefined
  setRemark3: (value: string) => void
  templateTest: any[] | undefined
  setTemplateTest: (value: any[] | undefined) => void
  identificationApprovalFlow: string
  setIdentificationApprovalFlow: (value: string) => void
  completeHeaderItem: any
  setCompleteHeaderItem: (value: any | undefined) => void
  base64Graphics: string | undefined
  setBase64Graphics: (value: string | undefined) => void
  generateNewReport: (value: GenerateNewReport) => void
}

export const GenerateReportContext =
  React.createContext<GenerateReportContextInterface>(
    {} as GenerateReportContextInterface
  )

interface GenerateReportProviderProps {
  children: ReactNode
}

const GenerateReportProvider = ({ children }: GenerateReportProviderProps) => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [site, setSite] = React.useState('')
  const mappedSites = SITES.map((s) => ({
    key: s.id,
    value: s.id?.toString() || '',
    label: s.name,
  }))
  const mappedTests = TESTS.map((s) => ({
    key: s.id,
    value: s.id?.toString() || '',
    label: s.name,
  }))
  const [test, setTest] = React.useState<string>('')
  const [tests, setTests] = React.useState<string[]>([])
  const [startDate, setStartDate] = React.useState<Date | undefined>(undefined)
  const [endDate, setEndDate] = React.useState<Date | undefined>(undefined)
  const [project, setProject] = React.useState('')
  const [projects, setProjects] = React.useState<string[]>([])
  const [orderItem, setOrderItem] = React.useState('')
  const [orderItems, setOrderItems] = React.useState<string[]>([])
  const [typeInspection, setTypeInspection] = React.useState('')
  const [typeInspections, setTypeInspections] = React.useState<string[]>([])
  const [type, setType] = React.useState('')
  const [types, setTypes] = React.useState<string[]>([])
  const [clientHeader, setClientHeader] = React.useState('')
  const [descriptionHeader, setDescriptionHeader] = React.useState('')
  const [projectHeader, setProjectHeader] = React.useState('')
  const [inspectionHeader, setInspectionHeader] = React.useState('')
  const [pedidoItemHeader, setPedidoItemHeader] = React.useState('')
  const [heatItems, setHeatItems] = React.useState<string[] | undefined>(
    undefined
  )
  const [testSelected, setTestSelected] = React.useState<TestProps | undefined>(
    undefined
  )
  const [processOrderItems, setProcessOrderItems] = React.useState<
    string[] | undefined
  >(undefined)
  const [pqLotItems, setPqLotItems] = React.useState<string[] | undefined>(undefined)
  const [resultItemsSelected, setResultItemsSelected] = React.useState<
    ReportItems[] | undefined
  >(undefined)
  const [currentItem, setCurrentItem] = React.useState<ReportItems>()
  const [remark1, setRemark1] = React.useState('')
  const [remark2, setRemark2] = React.useState('')
  const [remark3, setRemark3] = React.useState('')
  const [templateTest, setTemplateTest] = React.useState<any[] | undefined>(
    undefined
  )
  const [identificationApprovalFlow, setIdentificationApprovalFlow] =
    React.useState('')
  const [completeHeaderItem, setCompleteHeaderItem] = React.useState(undefined)
  const [base64Graphics, setBase64Graphics] = React.useState<
    string | undefined
  >(undefined)

  const generateNewReport = async (generateParam: GenerateNewReport) => {
    const elaborationPosition = generateParam.user?.userPosition ?? ' '
    const elaborationName = generateParam.user?.userName ?? ' '
    const completeHeaderItemToUse =
      generateParam.customFields?.completeHeaderItem ?? completeHeaderItem

    if (
      testSelected &&
      currentItem &&
      completeHeaderItemToUse &&
      resultItemsSelected &&
      generateParam.user &&
      generateParam.user.userPosition
    ) {
      try {
        const dateNow = getDateNow()
        const paramSite = SITES.find((s) => s.name === generateParam.site)

        if (!paramSite) throw new Error('Site not found')

        const testReport = getTest(testSelected.id)
        const customFields = {
          completeHeaderItem: completeHeaderItemToUse,
          customer: generateParam.customFields?.customer ?? clientHeader,
          description:
            generateParam.customFields?.description ?? descriptionHeader,
          inspection: generateParam.customFields?.inspection ?? inspectionHeader,
          project: generateParam.customFields?.project ?? projectHeader,
          orderItem: generateParam.customFields?.orderItem ?? pedidoItemHeader,
          justGenerate: generateParam.justGenerateValue,
          generateExcel: generateParam.generateExcel,
        } as CustomFields
        const userElaboration = {
          email: generateParam.user.email,
          name: elaborationPosition ?? generateParam.user.userPosition,
          description: elaborationName ?? generateParam.user.userName,
        }

        const isCorrosion =
          'typeCode' in (testSelected as object) &&
          testSelected?.typeCode === 'corrosion'

        const selected = isCorrosion
          ? orderBy(
              resultItemsSelected,
              [
                'corrida',
                'ordem_de_processo',
                'lote_tt',
                'ippn',
                'posicao_amostra',
                'id_ensaio',
                'n_cp',
              ],
              ['asc', 'asc', 'asc', 'asc', 'asc', 'asc', 'asc']
            )
          : orderBy(
              resultItemsSelected,
              ['ordem_de_processo', 'lote_tt', 'ippn', 'posicao_amostra'],
              ['asc', 'asc', 'asc', 'asc']
            )

        const resultSelected = await generateReportNewPost(
          testSelected,
          selected
        )

        const params = await testReport.getParameters(
          resultSelected,
          customFields,
          testSelected
        )

        const resultTemplate = await getTemplate({ id: 'template' })
        const generalContent = resultTemplate?.generalContent
        const remark1ToUse = generateParam.remark1 ?? remark1
        const remark2ToUse = generateParam.remark2 ?? remark2
        const remark3ToUse = generateParam.remark3 ?? remark3

        const readerReport = isCorrosion
          ? getReaderReport(generalContent, [], true)
          : undefined
        const approvalReport = getApprovalReport(
          generalContent,
          [],
          isCorrosion
        )
        const elaborationReport = getGeneralReport(generalContent, [
          {
            email: generateParam.user.email,
            name: elaborationPosition ?? generateParam.user.userPosition,
            description: elaborationName ?? generateParam.user.userName,
          },
        ])

        const reportNumberValue = await getReportNumber(
          paramSite,
          testSelected,
          generateParam.justGenerateValue
        )
        const fileName = `${`${testSelected.name}_${reportNumberValue}.pdf`}`
        const fileNameDD = `${`${testSelected.name}_${reportNumberValue}.json`}`

        const reportValue = await generateDocumentDefinition(
          params,
          dateNow,
          generalContent,
          paramSite,
          remark1ToUse,
          remark2ToUse,
          remark3ToUse,
          testSelected,
          approvalReport,
          elaborationReport,
          readerReport,
          reportNumberValue
        )

        // prepareAnnex

        if (userElaboration.email) {
          const signatureUser = await getSignatureUser({
            email: userElaboration.email,
          })
          const base64Signature = signatureUser.pathFile
            ? await getBase64FromUrl(signatureUser.pathFile)
            : signatureUser.fileBase64
          await addSignatureInDD(
            reportValue.documentDefinition.content[2].stack,
            userElaboration.email,
            0,
            signatureUser,
            base64Signature
          )
          if (
            reportValue.documentDefinition.content[3] &&
            reportValue.documentDefinition.content[3][2]?.stack
          ) {
            await addSignatureInDD(
              reportValue.documentDefinition.content[3][2].stack,
              userElaboration.email,
              0,
              signatureUser,
              base64Signature
            )
          }
        }

        const bkpLayout =
          reportValue.documentDefinition.content[1] &&
          reportValue.documentDefinition.content[1].layout
            ? reportValue.documentDefinition.content[1].layout
            : undefined
        const dd = JSON.parse(JSON.stringify(reportValue.documentDefinition))

        if (bkpLayout) dd.content[1].layout = bkpLayout

        // let response;
        // let fileName;

        if (!generateParam.justGenerateValue) {
          const responseFlowApproval = await sendToFlowApproval({
            test: testSelected,
            site: paramSite,
            selected,
            fileName,
            currentDate: getDateTimeNow().en,
            params,
            approvalReport,
            userElaboration,
            reportNumberValue,
            email: generateParam.user.email,
            user: generateParam.user.userName,
            customFields,
          })

          const file = await getFilePDF(
            JSON.parse(JSON.stringify(reportValue.documentDefinition)),
            fileName
          )

          const dataFile = await dealWithFile(
            file,
            `report/${testSelected.id}/${responseFlowApproval.idReportDocument}/${file.name}`,
            responseFlowApproval.idReportDocument,
            'path_file'
          )

          await updateReportDocument(dataFile) // create attachment on database

          const jsonDD = JSON.stringify(reportValue.documentDefinition, null, 2)
          const fileDD = new File(
            [new Blob([jsonDD], { type: 'text/plain' })],
            fileNameDD,
            { type: 'application/json' }
          )

          const dataFileDocumentDefinition = await dealWithFile(
            fileDD,
            `document_definition/${testSelected.id}/${responseFlowApproval.idReportDocument}/${fileDD.name}`,
            responseFlowApproval.idReportDocument,
            'path_document_definition'
          )

          await updateReportDocument(dataFileDocumentDefinition) // create attachment on database

          usersNotifications({
            test: testSelected,
            reportNumberValue,
            email: generateParam.user.email,
          })

          toast({
            title: 'Success',
            description: t('application.commons.success.operation'),
            type: 'success',
          })
        } else {
          await downloadPdfFile(reportValue.documentDefinition, fileName)
        }
      } catch (error) {
        console.log(error)
        if (isAxiosError(error)) {
          toast({
            title: 'Error',
            description: error.response?.data?.errorMessage ?? error.message,
            type: 'error',
          })
        } else {
          toast({
            title: 'Error',
            description: `${error}`,
            type: 'error',
          })
        }
      }
    }
  }

  return (
    <GenerateReportContext.Provider
      value={{
        site,
        setSite,
        mappedSites,
        test,
        setTest,
        mappedTests,
        tests,
        setTests,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        project,
        setProject,
        projects,
        setProjects,
        orderItem,
        setOrderItem,
        orderItems,
        setOrderItems,
        typeInspection,
        setTypeInspection,
        typeInspections,
        setTypeInspections,
        type,
        setType,
        types,
        setTypes,
        clientHeader,
        setClientHeader,
        descriptionHeader,
        setDescriptionHeader,
        projectHeader,
        setProjectHeader,
        inspectionHeader,
        setInspectionHeader,
        pedidoItemHeader,
        setPedidoItemHeader,
        currentItem,
        setCurrentItem,
        testSelected,
        setTestSelected,
        heatItems,
        setHeatItems,
        processOrderItems,
        setProcessOrderItems,
        pqLotItems,
        setPqLotItems,
        resultItemsSelected,
        setResultItemsSelected,
        remark1,
        setRemark1,
        remark2,
        setRemark2,
        remark3,
        setRemark3,
        templateTest,
        setTemplateTest,
        identificationApprovalFlow,
        setIdentificationApprovalFlow,
        completeHeaderItem,
        setCompleteHeaderItem,
        base64Graphics,
        setBase64Graphics,
        generateNewReport,
      }}
    >
      {children}
    </GenerateReportContext.Provider>
  )
}

function useGenerateReport(): GenerateReportContextInterface {
  return useContext(GenerateReportContext)
}

export { GenerateReportProvider, useGenerateReport }
